import { StoreSlice } from '../../utils/store-slice';
import produce from 'immer';
import { IChat, Message } from '../concept/chat';
import { ILogger } from '../concept/logger';
import { IRemoteChat, MessageDoc } from '../../../remote/apis/chat';
import { GetState } from 'zustand';

// Generic unsubscribe function type (compatible with Amplify Events)
type Unsubscribe = () => void;

interface IPrivate {
  unsub: Unsubscribe;
  appendMessage(doc: MessageDoc): void;
}

export const create =
  (remote: IRemoteChat): StoreSlice<IChat & IPrivate, ILogger> =>
    (set, get) => {
      return {
        unsub: () => { },
        appendMessage(doc: MessageDoc) {
          // Ignore read receipts - they're just for backend tracking
          if ((doc as any).type === 'read_receipt') {
            return;
          }

          set(
            produce((s: IChat) => {
              const message = s.messages.find(withMessageId(doc.messageId));
              if (message) {
                Object.assign(message, docToMessage(get)(doc));
              } else {
                s.messages.push(docToMessage(get)(doc));
              }
            })
          );
        },

        channelId: '',
        sender: '',
        messages: [],

        init(channelId: string, sender: string) {
          get().unsub();
          set({
            channelId,
            sender,
            messages: [],
            unsub: remote.onMessageSnapshot(channelId, get().appendMessage),
          });

          // Load existing messages from MongoDB
          remote.getMessages(channelId).then((existingMessages) => {
            console.log(`💬 [Chat Store] Loading ${existingMessages.length} existing messages`);
            existingMessages.forEach((msg) => get().appendMessage(msg));
          }).catch((error) => {
            console.error('💬 [Chat Store] Failed to load existing messages:', error);
          });
        },

        sendMessage(phase: string, content: string) {
          remote.sendMessage(get().channelId, phase, {
            content,
            sender: get().sender,
            timestamp: Date.now(),
          });
        },

        markAsRead(messageId: string) {
          // Optimistic update: mark as read immediately in local state
          set(
            produce((s: IChat) => {
              const message = s.messages.find(withMessageId(messageId));
              if (message && !message.viewers.includes(get().sender)) {
                message.viewers.push(get().sender);
                message.isRead = true;
              }
            })
          );

          // Then send to backend for persistence
          remote.markMessageAsRead(get().channelId, messageId, get().sender);
        },
      };
    };

const withMessageId = (id: string) => (target: { messageId: string }) =>
  target.messageId === id;

const docToMessage =
  (get: GetState<IChat>) =>
    (doc: MessageDoc): Message => ({
      channelId: doc.channelId,
      content: doc.content,
      isRead: doc.viewers?.includes(get().sender) ?? false,
      messageId: doc.messageId,
      sender: doc.sender,
      phase: doc.phase,
      timestamp: doc.timestamp,
      viewers: doc.viewers || [],
    });
